import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Paperclip, Image as ImageIcon, FileText, Mic, X, Camera, Sparkles, ChevronDown } from 'lucide-react';
import { showToast } from '@/utils/toast';
import axios from 'axios';

/**
 * ChatInput Component
 * Handles text input, attachments, voice recording, and message sending
 * 
 * @param {Object} props
 * @param {string} props.input - Current input text
 * @param {boolean} props.loading - Message sending in progress
 * @param {File|null} props.selectedImage - Selected image file
 * @param {File|null} props.selectedFile - Selected document file
 * @param {boolean} props.isVoiceMode - Voice mode active
 * @param {boolean} props.creditLimitReached - Credit limit reached
 * @param {boolean} props.isKidsUser - Kids theme active
 * @param {Function} props.onInputChange - Input change handler
 * @param {Function} props.onSend - Send message handler
 * @param {Function} props.onImageSelect - Image selection handler
 * @param {Function} props.onFileSelect - File selection handler
 * @param {Function} props.onVoiceToggle - Voice mode toggle handler
 * @param {Function} props.onClearAttachment - Clear attachment handler
 */
function ChatInput({
  input,
  loading,
  selectedImage,
  selectedFile,
  isVoiceMode,
  creditLimitReached,
  isKidsUser,
  creditStatus,
  useLibrary = false,
  onInputChange,
  onSend,
  onImageSelect,
  onFileSelect,
  onVoiceToggle,
  onClearAttachment,
  onAgentExecute,
  onOpenCreditModal,
  onLibraryToggle
}) {
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [executingAgent, setExecutingAgent] = useState(false);
  const [showInsufficientCreditModal, setShowInsufficientCreditModal] = useState(false);
  const [insufficientCreditAgent, setInsufficientCreditAgent] = useState(null);
  
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const agentMenuRef = useRef(null);
  
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);
  
  // Fetch agents when menu opens
  useEffect(() => {
    if (showAgentMenu && agents.length === 0) {
      fetchAgents();
    }
  }, [showAgentMenu]);
  
  // Close agent menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (agentMenuRef.current && !agentMenuRef.current.contains(event.target)) {
        setShowAgentMenu(false);
      }
    };
    
    if (showAgentMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAgentMenu]);
  
  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const token = localStorage.getItem('access_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      console.log('🔍 Fetching agents...', { token: token ? 'exists' : 'missing' });
      
      const response = await axios.get(`/api/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Agents response:', response.data);
      
      if (response.data.success) {
        setAgents(response.data.agents);
        console.log('✅ Agents set:', response.data.agents);
      }
    } catch (error) {
      console.error('❌ Failed to fetch agents:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoadingAgents(false);
    }
  };
  
  const handleAgentSelect = (agent) => {
    // Kredi kontrolü yap
    if (creditStatus && creditStatus.total_available < agent.credit_cost) {
      setInsufficientCreditAgent(agent);
      setShowInsufficientCreditModal(true);
      setShowAgentMenu(false);
      return;
    }
    setSelectedAgent(agent);
    setShowAgentMenu(false);
  };
  
  const handleAgentExecute = async () => {
    if (!selectedAgent || !input.trim()) return;
    
    const userQuery = input.trim();
    
    // Show loading animation immediately for deep research
    if (selectedAgent.id === 'deep_research' && onAgentExecute) {
      onAgentExecute({
        showLoading: true,
        agent_name: selectedAgent.name,
        user_query: userQuery  // Pass user query to parent
      });
    }
    
    // Clear input immediately
    onInputChange('');
    
    setExecutingAgent(true);
    let hasError = false;
    let isTimeout = false;
    
    try {
      const token = localStorage.getItem('access_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await axios.post(
        `/api/agents/execute`,
        {
          agent_id: selectedAgent.id,
          query: input.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 360000 // 6 minutes timeout for deep research
        }
      );
      
      if (response.data.success) {
        // Clear selection (input already cleared)
        setSelectedAgent(null);
        // Pass result to parent
        if (onAgentExecute) {
          onAgentExecute(response.data);
        }
      } else if (response.data.error === 'insufficient_credits') {
        // Remove loading animation
        if (onAgentExecute) {
          onAgentExecute({ clearLoading: true });
        }
        showToast.error(response.data.message);
        setSelectedAgent(null);
      } else {
        // Remove loading animation for other errors
        if (onAgentExecute) {
          onAgentExecute({ clearLoading: true });
        }
        showToast.error(response.data.message || 'Bir hata oluştu');
        setSelectedAgent(null);
      }
    } catch (error) {
      hasError = true;
      console.error('Agent execution failed:', error);
      
      // Don't show error for timeout - research is still running
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        isTimeout = true;
        console.log('Request timeout - research continues in background');
        showToast.info('Araştırma devam ediyor...', 'Sonuç biraz daha uzun sürebilir');
        // Don't clear loading state - let it continue
        return;
      }
      
      // Retry logic for server errors
      if (error.response?.status === 500 && !error.config?.retryCount) {
        console.log('Server error, retrying in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        error.config = error.config || {};
        error.config.retryCount = 1;
        return handleAgentExecute();
      }
      
      showToast.error('Agent çalıştırılamadı');
      setExecutingAgent(false);
      setSelectedAgent(null);
    } finally {
      // Only clear if not timeout
      if (!hasError || !isTimeout) {
        setExecutingAgent(false);
      }
    }
  };
  
  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e) => {
    // Enter to send (Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  /**
   * Handle send message
   */
  const handleSend = () => {
    if (!input.trim() && !selectedImage && !selectedFile) return;
    if (loading || executingAgent) return;
    if (creditLimitReached) {
      showToast.warning('Kredi Limiti', 'Aylık kredi limitinize ulaştınız');
      return;
    }
    
    // If agent is selected, execute agent instead of normal chat
    if (selectedAgent) {
      handleAgentExecute();
    } else {
      onSend();
    }
  };
  
  /**
   * Handle image selection
   */
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast.error('Geçersiz Dosya', 'Lütfen bir resim dosyası seçin');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Dosya Çok Büyük', 'Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }
    
    onImageSelect(file);
    setShowAttachmentMenu(false);
  };
  
  /**
   * Handle camera capture
   */
  const handleCameraChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast.error('Geçersiz Dosya', 'Lütfen bir resim dosyası seçin');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Dosya Çok Büyük', 'Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }
    
    onImageSelect(file);
    setShowAttachmentMenu(false);
  };
  
  /**
   * Handle file selection
   */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('Dosya Çok Büyük', 'Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }
    
    onFileSelect(file);
    setShowAttachmentMenu(false);
  };
  
  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  return (
    <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-6xl mx-auto p-6">
        {/* Attachment Preview */}
        {(selectedImage || selectedFile) && (
          <div className="mb-3 p-3 bg-muted/50 rounded-xl flex items-center justify-between border">
            <div className="flex items-center gap-2">
              {selectedImage && (
                <>
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{selectedImage.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatFileSize(selectedImage.size)})
                  </span>
                </>
              )}
              {selectedFile && (
                <>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatFileSize(selectedFile.size)})
                  </span>
                </>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAttachment}
              aria-label="Eki kaldır"
              className="h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Input Area */}
        <div className="relative">
          <div className="flex items-end gap-3 p-4 bg-card border rounded-2xl shadow-sm hover:shadow-md transition-shadow">
            {/* Attachment Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                disabled={loading || isVoiceMode}
                aria-label="Dosya ekle"
                className="h-9 w-9 rounded-xl"
              >
                <Paperclip className="h-5 w-5" />
              </Button>
              
              {showAttachmentMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-popover border rounded-xl shadow-lg p-2 min-w-[160px] z-10">
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent rounded-lg text-sm transition-colors"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <ImageIcon className="h-4 w-4" />
                    <span>Resim Ekle</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent rounded-lg text-sm transition-colors"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4" />
                    <span>Fotoğraf Çek</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent rounded-lg text-sm transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Dosya Ekle</span>
                  </button>
                </div>
              )}
              
              {/* Hidden file inputs */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleCameraChange}
              />
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            
            {/* Text Input - Genişletilmiş */}
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Herhangi bir şey sorun..."
              disabled={loading || isVoiceMode || executingAgent}
              className="flex-1 min-h-[60px] max-h-[200px] resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
              rows={2}
              aria-label="Mesaj giriş alanı"
              aria-describedby={creditLimitReached ? "credit-limit-warning" : undefined}
            />
            
            {/* Voice Button */}
            {!isKidsUser && (
              <Button
                variant={isVoiceMode ? "default" : "ghost"}
                size="icon"
                onClick={onVoiceToggle}
                disabled={loading}
                aria-label={isVoiceMode ? "Ses modunu kapat" : "Ses modunu aç"}
                aria-pressed={isVoiceMode}
                className="h-9 w-9 rounded-xl"
              >
                <Mic className={`h-5 w-5 ${isVoiceMode ? 'animate-pulse' : ''}`} />
              </Button>
            )}
            
            {/* Send Button */}
            <Button
              onClick={handleSend}
              disabled={loading || executingAgent || creditLimitReached || (!input.trim() && !selectedImage && !selectedFile)}
              size="icon"
              aria-label={selectedAgent ? `${selectedAgent.name} ile ara` : "Mesaj gönder"}
              className="h-9 w-9 rounded-xl"
            >
              {(loading || executingAgent) ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : selectedAgent ? (
                <Sparkles className="h-5 w-5" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
        
        {/* Agent Selector & Library Toggle - Input'un Altında */}
        <div className="mt-3 flex items-center gap-3">
          {/* Agent Selector */}
          <div className="relative" ref={agentMenuRef}>
            <button
              onClick={() => {
                console.log('🎯 Agent button clicked, current state:', showAgentMenu);
                setShowAgentMenu(!showAgentMenu);
              }}
              disabled={loading || isVoiceMode || executingAgent}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="h-4 w-4" />
              <span className="font-medium">
                {executingAgent ? 'Çalışıyor...' : selectedAgent ? selectedAgent.name : 'Agent Seç'}
              </span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAgentMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showAgentMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-popover border rounded-xl shadow-lg p-2 min-w-[280px] z-10">
                {loadingAgents ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Yükleniyor...
                  </div>
                ) : agents.length > 0 ? (
                  <>
                    {console.log('📋 Rendering agents:', agents)}
                    {agents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => handleAgentSelect(agent)}
                        className="w-full px-3 py-2.5 text-left hover:bg-accent rounded-lg transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-base">{agent.icon}</span>
                            <span className="text-sm font-medium">{agent.name}</span>
                          </div>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                            {agent.credit_cost} kredi
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-6">
                          {agent.description}
                        </p>
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Agent bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Library Toggle */}
          {onLibraryToggle && (
            <button
              onClick={onLibraryToggle}
              disabled={loading || executingAgent}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                useLibrary
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/40'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title={useLibrary ? 'Kütüphane kullanılıyor' : 'Kütüphane kullan'}
            >
              <FileText className="h-4 w-4" />
              <span className="font-medium">
                {useLibrary ? 'Kütüphane Aktif' : 'Kütüphane Kullan'}
              </span>
            </button>
          )}
        </div>
        
        {/* Credit Limit Warning */}
        {creditLimitReached && (
          <p id="credit-limit-warning" className="text-xs text-orange-600 dark:text-orange-400 mt-3 text-center">
            ⚠️ Aylık kredi limitinize ulaştınız. PRO'ya yükseltin!
          </p>
        )}
      </div>

      {/* Insufficient Credit Modal */}
      {showInsufficientCreditModal && insufficientCreditAgent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-sm w-full shadow-2xl border border-gray-200 dark:border-gray-800">
            {/* Header */}
            <div className="p-5 pb-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Yetersiz Kredi
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Bu özellik için daha fazla kredi gerekiyor
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 pb-5">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Özellik</span>
                  <span className="font-medium text-gray-900 dark:text-white">{insufficientCreditAgent.name}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Gerekli</span>
                  <span className="font-semibold text-orange-600 dark:text-orange-400">{insufficientCreditAgent.credit_cost} kredi</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Mevcut</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creditStatus?.total_available || 0} kredi</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowInsufficientCreditModal(false);
                    setInsufficientCreditAgent(null);
                  }}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Kapat
                </button>
                <button
                  onClick={() => {
                    setShowInsufficientCreditModal(false);
                    setInsufficientCreditAgent(null);
                    if (onOpenCreditModal) {
                      onOpenCreditModal();
                    }
                  }}
                  className="flex-1 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-all"
                >
                  Kredi Satın Al
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with React.memo
export default React.memo(ChatInput, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.input === nextProps.input &&
    prevProps.loading === nextProps.loading &&
    prevProps.selectedImage === nextProps.selectedImage &&
    prevProps.selectedFile === nextProps.selectedFile &&
    prevProps.isVoiceMode === nextProps.isVoiceMode &&
    prevProps.creditLimitReached === nextProps.creditLimitReached &&
    prevProps.isKidsUser === nextProps.isKidsUser
  );
});
